import { useLazyQuery, useQuery } from "@apollo/client";
import { ExtractionReviewValues } from "components/Document/ExtractionReview";
import { gql } from "generated-graphql";
import { Issue } from "generated-graphql/graphql";
import { useCallback, useRef, useState } from "react";

import { GENERATOR_EPA_ID_KEY, lineNumberIssues, toText } from "./extractionReview";

export const BILL_OF_LADING_REVIEW_VALIDATOR = gql(`
  query BillOfLadingReviewValidator($documentId: ID!, $facilityId: ID, $generatorEpaId: String) {
    billOfLadingReviewValidator(
      documentId: $documentId
      facilityId: $facilityId
      generatorEpaId: $generatorEpaId
    ) {
      ...issue
    }
  }
`);

const NO_ISSUES: Issue[] = [];

/**
 * The generator EPA ID's issues for the bill review grid, as manifest review
 * shows them: registered, in-tenant, and whether it belongs to a facility other
 * than the one picked. `initialIssues` come from the value and facility the form
 * opens with. `validate` is the grid's validator: the line-number issues, plus
 * the EPA ID's issues re-checked when the typed value or the picked facility
 * changes. Warn-and-allow, so the dialog passes `GENERATOR_EPA_ID_KEY` as a
 * warning field.
 */
export function useBillOfLadingEpaIdIssues(
  documentId: string | undefined,
  initialGeneratorEpaId: string | null | undefined,
  facilityId?: string
) {
  // Later picks re-check through `validate`, so a refetch here can't reset the
  // issues under a form that has moved on.
  const [openedFacilityId] = useState(facilityId);
  const { data } = useQuery(BILL_OF_LADING_REVIEW_VALIDATOR, {
    variables: {
      documentId: documentId ?? "",
      facilityId: openedFacilityId || null,
      generatorEpaId: initialGeneratorEpaId ?? null,
    },
    skip: !documentId || !initialGeneratorEpaId,
  });
  const [runValidation] = useLazyQuery(BILL_OF_LADING_REVIEW_VALIDATOR, {
    fetchPolicy: "no-cache",
  });

  // The grid validates on every edit; only a changed EPA ID or facility needs the server.
  const lastChecked = useRef<{ key: string; issues: Issue[] } | null>(null);

  const validate = useCallback(
    async (reviewed: ExtractionReviewValues): Promise<Issue[]> => {
      const lineIssues = lineNumberIssues(reviewed);
      const generatorEpaId = toText(reviewed.values[GENERATOR_EPA_ID_KEY]);
      if (!documentId || !generatorEpaId) return lineIssues;
      const key = `${facilityId ?? ""}|${generatorEpaId}`;
      if (lastChecked.current?.key !== key) {
        const { data: checked } = await runValidation({
          variables: { documentId, facilityId: facilityId || null, generatorEpaId },
        });
        lastChecked.current = {
          key,
          issues: checked?.billOfLadingReviewValidator ?? NO_ISSUES,
        };
      }
      return [...lineIssues, ...lastChecked.current.issues];
    },
    [documentId, facilityId, runValidation]
  );

  return { initialIssues: data?.billOfLadingReviewValidator ?? NO_ISSUES, validate };
}
